import { useCallback, useEffect, useState } from 'react'
import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import {
  getQuickPassSavedSearchesForUser,
  runQuickPassSavedSearch,
} from '../../_data/quickpass-saved-searches'
import {
  formatLastRun,
  summariseCriteria,
} from '../../pages/saved-searches/helpers'
import { QuickPassSavedSearch } from '../../_types/quickpass-saved-search'
import { SavedSearchesSummaryState } from './types'
import { getFrequencyLabel } from './helpers'
import {
  Card,
  CardActions,
  CardCriteria,
  CardGrid,
  CardMatches,
  CardMeta,
  CardName,
  CardTopRow,
  EmptyBody,
  EmptyEmoji,
  EmptyTile,
  EmptyTitle,
  FrequencyChip,
  RunNowButton,
  SkeletonRow,
  SummaryHeader,
  SummaryLink,
  SummaryRoot,
  SummaryTitle,
  ViewLink,
} from './styled'

const DASHBOARD_VISIBLE_LIMIT = 3

/**
 * Dashboard widget summarising the renter's saved searches. Shows up to three
 * cards (most recently updated first) with criteria, match count, last-run
 * timestamp, a Run-now button, and a link into the saved-searches detail page.
 * When the renter has none, shows a CTA tile pointing at /saved-searches.
 *
 * @returns {JSX.Element | null} null while auth is loading
 */
const SavedSearchesSummary = (): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const { refresh: refreshNotifications } = useQuickPassNotifications()
  const [state, setState] = useState<SavedSearchesSummaryState>({
    savedSearches: [],
    isLoading: true,
    isError: false,
    runningSavedSearchId: null,
  })

  const loadSavedSearches = useCallback(
    async (quickPassUserId: string): Promise<void> => {
      setState(previous => ({ ...previous, isLoading: true, isError: false }))

      try {
        const fetched = await getQuickPassSavedSearchesForUser(quickPassUserId)
        setState(previous => ({
          ...previous,
          savedSearches: fetched,
          isLoading: false,
        }))
      } catch (error) {
        console.error(
          '[saved-searches-summary] failed to load saved searches',
          error,
        )
        setState(previous => ({
          ...previous,
          savedSearches: [],
          isLoading: false,
          isError: true,
        }))
      }
    },
    [],
  )

  useEffect(() => {
    if (quickPassUser === null) {
      setState({
        savedSearches: [],
        isLoading: false,
        isError: false,
        runningSavedSearchId: null,
      })
      return
    }

    void loadSavedSearches(quickPassUser.id)
  }, [quickPassUser, loadSavedSearches])

  const handleRunNow = useCallback(
    async (savedSearch: QuickPassSavedSearch): Promise<void> => {
      if (quickPassUser === null) {
        return
      }

      setState(previous => ({
        ...previous,
        runningSavedSearchId: savedSearch.id,
      }))

      try {
        const result = await runQuickPassSavedSearch(
          savedSearch.id,
          quickPassUser.id,
        )
        setState(previous => ({
          ...previous,
          savedSearches: previous.savedSearches.map(entry =>
            entry.id === result.savedSearch.id ? result.savedSearch : entry,
          ),
          runningSavedSearchId: null,
        }))

        if (result.notificationsWritten > 0) {
          await refreshNotifications()
        }
      } catch (error) {
        console.error(
          '[saved-searches-summary] failed to run saved search',
          error,
        )
        setState(previous => ({ ...previous, runningSavedSearchId: null }))
      }
    },
    [quickPassUser, refreshNotifications],
  )

  if (quickPassUser === null) {
    return null
  }

  const visibleSearches = state.savedSearches.slice(0, DASHBOARD_VISIBLE_LIMIT)
  const hasSearches = visibleSearches.length > 0

  return (
    <SummaryRoot>
      <SummaryHeader>
        <SummaryTitle>Saved searches</SummaryTitle>
        {hasSearches === true ? (
          <SummaryLink to="/saved-searches">Manage all</SummaryLink>
        ) : null}
      </SummaryHeader>

      {state.isLoading === true ? (
        <CardGrid>
          <SkeletonRow />
          <SkeletonRow />
        </CardGrid>
      ) : null}

      {state.isLoading === false && hasSearches === true ? (
        <CardGrid>
          {visibleSearches.map(savedSearch => {
            const isRunning = state.runningSavedSearchId === savedSearch.id
            const matchCount = savedSearch.lastMatchListingIds.length
            return (
              <Card key={savedSearch.id}>
                <CardTopRow>
                  <CardName>{savedSearch.name}</CardName>
                  <FrequencyChip>
                    {getFrequencyLabel(savedSearch.frequency)}
                  </FrequencyChip>
                </CardTopRow>
                <CardCriteria>{summariseCriteria(savedSearch)}</CardCriteria>
                {matchCount > 0 ? (
                  <CardMatches>
                    {matchCount} current match
                    {matchCount === 1 ? '' : 'es'}
                  </CardMatches>
                ) : (
                  <CardMeta>No matches yet — we&apos;ll ping you.</CardMeta>
                )}
                <CardMeta>{formatLastRun(savedSearch.lastRunAt)}</CardMeta>
                <CardActions>
                  <RunNowButton
                    type="button"
                    disabled={isRunning}
                    onClick={() => {
                      void handleRunNow(savedSearch)
                    }}
                  >
                    {isRunning === true ? 'Running…' : 'Run now'}
                  </RunNowButton>
                  <ViewLink to="/saved-searches">View matches</ViewLink>
                </CardActions>
              </Card>
            )
          })}
        </CardGrid>
      ) : null}

      {state.isLoading === false && hasSearches === false ? (
        <EmptyTile to="/saved-searches">
          <EmptyEmoji aria-hidden="true">
            <Icon icon={faMagnifyingGlass} label="Magnifying glass" />
          </EmptyEmoji>
          <EmptyTitle>Save a search, get pinged on matches</EmptyTitle>
          <EmptyBody>
            Saved searches scan your imports + future inventory in the
            background. Set one up once and we&apos;ll surface new fits the
            moment they land.
          </EmptyBody>
        </EmptyTile>
      ) : null}
    </SummaryRoot>
  )
}

export default SavedSearchesSummary
